import { useEffect, useRef } from 'react'
import { useEventBus } from 'hooks/useEventBus'
import type { EventBusPayload } from 'services/eventBusService'
import useStore from 'state/knovStore'
import toast from 'utils/toast'
import { publishSwapStatus, SwapStatusEvent } from 'lib/cryptoSwapEvents'
import { coinNetwork, coinTicker, formatBsv, sentAmountShort } from 'lib/walletSwapMath'

const PAYMENT_STATUSES = new Set(['creating', 'new', 'waiting'])

/**
 * Session-wide listener for `crypto_swap_status` events (scoped to the current
 * user server-side by ChangellySwapTracker). A funding swap outlives the wallet
 * screen — unpaid for hours, then minutes of confirmations — so the moments
 * that need the user surface here as toasts wherever they are, and every
 * update is relayed to the wallet sheet through lib/cryptoSwapEvents so an open
 * wallet flips in place without polling.
 */
export default function CryptoSwapStatusListener() {
    const { getTopic$, isConnected } = useEventBus()
    const announced = useRef(new Set<string>())

    useEffect(() => {
        if (!isConnected) return

        const subscription = getTopic$('crypto_swap_status').subscribe(
            (payload: EventBusPayload) => {
                const event = ((payload as any)?.payload || payload) as SwapStatusEvent
                if (!event || typeof event.id !== 'string' || typeof event.status !== 'string')
                    return

                publishSwapStatus(event)

                const key = `${event.id}:${event.status}`
                if (announced.current.has(key)) return
                announced.current.add(key)

                const coin = coinTicker(event.from_currency)
                const options = {
                    kind: `crypto-swap:${event.id}`,
                    actionLabel: 'View',
                    cancelLabel: 'Dismiss',
                    onAction: () =>
                        useStore
                            .getState()
                            .actions.modalActions.openWalletModal({ openSwapId: event.id }),
                }
                const previous = event.previous_status || ''

                switch (event.status) {
                    case 'confirming':
                        if (!PAYMENT_STATUSES.has(previous)) return
                        toast.info('Payment received', {
                            ...options,
                            detail: `Confirming ${sentAmountShort(event)} on the ${coinNetwork(
                                event.from_currency,
                                event.currency_label,
                            )} network`,
                        })
                        return
                    case 'finished': {
                        const bsv = formatBsv(event.actual_bsv || event.expected_bsv)
                        toast.success('BSV arrived', {
                            ...options,
                            durationMs: 8000,
                            detail: `${
                                bsv ? `${bsv} BSV` : 'BSV'
                            } from your ${coin} swap is in your wallet`,
                        })
                        return
                    }
                    case 'hold':
                        toast.error('Swap needs verification', {
                            ...options,
                            detail: `Changelly is holding your ${coin} swap for identity checks`,
                        })
                        return
                    case 'failed':
                        toast.error('Swap failed', {
                            ...options,
                            detail: `Your ${coin} swap did not complete — contact Changelly support before sending again`,
                        })
                        return
                    case 'refunded':
                        toast.error('Swap refunded', {
                            ...options,
                            detail: `Changelly refunded your ${coin} swap`,
                        })
                        return
                    case 'expired':
                    case 'overdue':
                        toast.error('Swap expired', {
                            ...options,
                            detail: `The payment window for your ${coin} swap closed — start a new swap`,
                        })
                        return
                    default:
                        return
                }
            },
        )

        return () => subscription.unsubscribe()
    }, [isConnected, getTopic$])

    return null
}
